// ── CHAT PAGE (fp-nofodesk-9) — extracted from App.js ──
// Presentational: chat/streaming state stays in GrantOS (it is shared with
// the sidebar session list, the review modal, and the onboarding replay);
// this component renders the extract/chat surface from props. Prop names
// match the original identifiers so the JSX moved verbatim.
import { LogoMark } from "../LandingPage";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { useWindowWidth } from "../hooks/useWindowWidth";
import { useSubscription } from "../context/SubscriptionContext";
import { API, apiFetch, generateId } from "../utils/api";
import MessageBubble from "../components/MessageBubble";
import DeadlineAlertBanner from "../components/DeadlineAlertBanner";

// ── GRANT TABS ───────────────────────────────────────────────
// One tab per grant, like browser tabs. Pasting a new grant opens a new tab
// rather than overwriting the one you are reading — extracting a second
// grant used to replace the first, losing the comparison you were making.
function GrantTabs({ T, sessions, currentSessionId, onLoad, onClose, onNew, isMobile }) {
  // Only sessions with real content earn a tab; a freshly opened blank one
  // shows as "New grant" so the active tab is never unlabelled.
  const tabs = (sessions || []).filter(s => s.id === currentSessionId || (s.messages && s.messages.length));
  if (!tabs.length) return null;

  return (
    <div style={{
      display: "flex", alignItems: "stretch", gap: 2, flexShrink: 0,
      background: T.bgHeader, borderBottom: `1px solid ${T.borderDim}`,
      padding: "0 8px", overflowX: "auto", overflowY: "hidden",
    }}>
      {tabs.map(s => {
        const active = s.id === currentSessionId;
        const label  = (s.title && s.title !== "Untitled Extraction") ? s.title : "New grant";
        return (
          <div key={s.id}
            onClick={() => !active && onLoad(s)}
            title={label}
            style={{
              display: "flex", alignItems: "center", gap: 7, cursor: active ? "default" : "pointer",
              padding: isMobile ? "9px 10px" : "10px 12px", maxWidth: isMobile ? 150 : 220, minWidth: 0,
              background: active ? T.bgApp : "transparent",
              borderTop: `2px solid ${active ? TOKENS.gold : "transparent"}`,
              borderLeft: `1px solid ${active ? T.borderDim : "transparent"}`,
              borderRight: `1px solid ${active ? T.borderDim : "transparent"}`,
              marginBottom: -1,
            }}>
            <span style={{
              fontFamily: FONT_UI, fontSize: 12.5, fontWeight: active ? 600 : 400,
              color: active ? T.text : T.textMuted,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0,
            }}>{label}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onClose(s.id); }}
              aria-label={`Close ${label}`}
              style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                lineHeight: 1, fontSize: 15, flexShrink: 0,
                color: active ? T.textMuted : T.textFaint,
              }}>×</button>
          </div>
        );
      })}
      <button onClick={onNew} title="Extract another grant" aria-label="New grant tab"
        style={{
          background: "none", border: "none", cursor: "pointer", padding: "0 12px",
          color: T.textMuted, fontSize: 18, lineHeight: 1, flexShrink: 0,
        }}>+</button>
    </div>
  );
}

export default function ChatPage({
  messages, orgProfile, loading, hasNOFO, isStreaming,
  input, setInput, chatMode, setChatMode, writingStyle, setWritingStyle,
  currentGrantData, alertDismissed, setAlertDismissed,
  bottomRef, inputRef, fileRef, attachFile,
  sendMessage, stopStreaming, handleKeyDown, setActiveView,
  handleMiniProfileSave, handleDeadlineSave, markChecklistItem,
  sessions, currentSessionId, onLoadSession, onDeleteSession, onNewChat,
}) {
  const T        = useT();
  const isMobile = useWindowWidth() < 768;
  const appSub   = useSubscription();
  return (
    <>
              <GrantTabs
                T={T} isMobile={isMobile}
                sessions={sessions} currentSessionId={currentSessionId}
                onLoad={onLoadSession} onClose={onDeleteSession} onNew={onNewChat}
              />
              <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 12px" : "20px 24px" }}>
                {/* Feature 1: Deadline alert banner */}
                {!alertDismissed && (
                  <DeadlineAlertBanner
                    activeGrants={[]}
                    applications={[]}
                    tracked={[]}
                    onDismiss={() => setAlertDismissed(true)}
                  />
                )}
                {messages.length === 0 && (
                  <div style={{ textAlign: "center", paddingTop: isMobile ? 36 : 60, maxWidth: 560, margin: "0 auto", boxSizing: "border-box" }}>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
                      <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(91,79,233,0.1)", border: "1px solid rgba(91,79,233,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <LogoMark size={26} />
                      </div>
                    </div>
                    <div style={{ fontSize: isMobile ? 18 : 21, color: T.text, marginBottom: 10, fontFamily: FONT_H, lineHeight: 1.4 }}>
                      Paste a grant URL or drop a PDF — NofoDesk extracts every field in ~30 seconds.
                    </div>
                    <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT_UI, lineHeight: 1.6, marginBottom: 20 }}>
                      Deadline tracking, auto checklist, eligibility check and proposal drafting start from the extraction.
                    </div>
                    <button
                      data-tour="sample-button"
                      onClick={() => window.dispatchEvent(new CustomEvent("nofodesk:run-sample"))}
                      style={{ padding: "10px 22px", background: "rgba(91,79,233,0.12)", border: "1px solid rgba(91,79,233,0.45)", borderRadius: 8, color: TOKENS.slateBlue, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 40 }}>
                      Try it on a sample NOFO →
                    </button>
                    {!orgProfile && (
                      <div style={{ marginTop: 18, fontSize: 12, color: T.textMuted, fontFamily: FONT_UI }}>
                        <button onClick={() => setActiveView("settings")} style={{ background: "none", border: "none", color: TOKENS.slateBlue, cursor: "pointer", fontSize: 12, textDecoration: "underline", fontFamily: FONT_UI, padding: 0 }}>
                          Set up your organisation profile
                        </button>{" "}
                        to enable eligibility checks.
                      </div>
                    )}
                    <div style={{ marginTop: 24, display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                      {["PEPFAR", "USAID", "NIH", "Gates Foundation", "WHO/TDR", "CDC"].map(d => (
                        <span key={d} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 4, border: `1px solid ${T.border}`, color: T.textMuted, fontFamily: FONT_MONO, letterSpacing: "0.04em" }}>{d}</span>
                      ))}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 10, color: T.textFaint, fontFamily: FONT_MONO, letterSpacing: "0.06em", textTransform: "uppercase" }}>Purpose-built donor schemas</div>
                  </div>
                )}

                {messages.map((msg, i) => <MessageBubble key={msg.id || i} msg={msg} orgProfile={orgProfile} onMiniProfileSave={handleMiniProfileSave} onDeadlineSave={handleDeadlineSave} onAddToPipeline={async (entry) => { const newApp = { id: generateId(), sn: 0, title: entry.grantName || "", funder: entry.funder || "", amount: entry.amount || "", rfaNumber: entry.notes || "", deadline: "", submittedDate: "", status: "Not Submitted", primeOrSub: "Prime", pi: "", poc: "", awardStatus: "Pending", remark: "" }; apiFetch(`${API}/db/pipeline-apps`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ record: newApp }) }).catch(() => {}); markChecklistItem("application"); }} />)}

                {loading && !messages.some(m => m.type === "progress" || m.type === "streaming") && (
                  <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8, gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(91,79,233,0.12)", border: "1px solid rgba(91,79,233,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                      <LogoMark size={15} />
                    </div>
                    <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, padding: "12px 18px", borderRadius: "4px 16px 16px 16px", fontSize: 13, color: T.textMuted, fontFamily: FONT_UI }}>
                      <span style={{ animation: "pulse 1.5s infinite", display: "inline-block" }}>Preparing…</span>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* ── Input area ── */}
              <div style={{ padding: isMobile ? "10px 10px 14px" : "14px 24px 20px", borderTop: `1px solid ${T.borderDim}`, background: T.bgHeader, flexShrink: 0 }}>
                {/* Mode toggle bar */}
                <div style={{ maxWidth: isMobile ? "100%" : 860, margin: "0 auto 10px", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  {/* Chat vs Propose toggle */}
                  <div style={{ display: "flex", background: T.bgApp, border: `1px solid ${T.border}`, borderRadius: 8, padding: 2, gap: 2 }}>
                    {[
                      { id: "chat",    label: "💬 Chat" },
                      { id: "propose", label: "✍️ Develop Proposal" },
                    ].map(m => (
                      <button key={m.id} data-tour={m.id === "propose" ? "mode-propose" : undefined} onClick={() => {
                        if (m.id === "propose" && !appSub.canPropose) {
                          window.dispatchEvent(new CustomEvent("nofodesk:open-pricing"));
                          return;
                        }
                        setChatMode(m.id);
                      }} style={{
                        padding: "6px 12px", borderRadius: 6, border: "none", minHeight: 30,
                        background: chatMode === m.id ? TOKENS.gold : "transparent",
                        color: chatMode === m.id ? "#fff" : T.textMuted,
                        fontSize: 12, fontWeight: chatMode === m.id ? 600 : 400,
                        cursor: "pointer", fontFamily: FONT_UI, transition: "all 0.15s", whiteSpace: "nowrap",
                      }}>{m.label}</button>
                    ))}
                  </div>
                  {/* Writing style selector — only in propose mode */}
                  {chatMode === "propose" && (
                    <select value={writingStyle} onChange={e => setWritingStyle(e.target.value)} style={{
                      padding: "4px 10px", background: T.bgApp, border: `1px solid ${T.border}`,
                      borderRadius: 6, color: T.textMuted, fontSize: 12, fontFamily: FONT_UI,
                      outline: "none", cursor: "pointer",
                    }}>
                      <option value="professional">Professional Prose</option>
                      <option value="narrative">Narrative / Storytelling</option>
                      <option value="results-based">Results-Based</option>
                      <option value="logical-framework">Logical Framework (LogFrame)</option>
                    </select>
                  )}
                  {chatMode === "propose" && (
                    <div style={{ fontSize: 11, color: "#a78bfa", marginLeft: 4 }}>
                      {currentGrantData ? `📋 Using: ${(currentGrantData.opportunityIdentification?.callTitle || "loaded grant").slice(0, 40)}` : "No grant loaded — works best after extracting a NOFO"}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", maxWidth: isMobile ? "100%" : 860, margin: "0 auto" }}>
                <div className="nd-chat-input" data-tour="chat-input" style={{ flex: 1, background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "10px 14px", transition: "border-color 0.2s, box-shadow 0.2s" }}>
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      chatMode === "propose"
                        ? "Describe the proposal section you need — e.g. 'Write a compelling problem statement for a PEPFAR DREAMS grant targeting adolescent girls in Lagos' or 'Draft a full project narrative for water sanitation in Northern Ghana'…"
                        : hasNOFO ? "Ask a question about this grant..." : "Say hi, ask about grants, or paste a URL to analyze..."
                    }
                    rows={chatMode === "propose" ? (isMobile ? 4 : 3) : (isMobile ? 3 : 2)}
                    style={{ width: "100%", background: "transparent", border: "none", color: T.textBody, fontSize: isMobile ? 16 : 14, fontFamily: "inherit", resize: "none", outline: "none", lineHeight: 1.5 }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                    <div>
                      <input type="file" accept=".pdf,.docx,.xlsx,.xls" ref={fileRef} onChange={attachFile} style={{ display: "none" }} />
                      {chatMode !== "propose" && (
                        <button data-tour="attach-file" onClick={() => fileRef.current?.click()} style={{ padding: "4px 10px", background: "transparent", border: "none", color: T.textShadow, cursor: "pointer", fontSize: 12 }}>
                          📎 {isMobile ? "File" : "Attach File"}
                        </button>
                      )}
                    </div>
                    {!isMobile && <div style={{ fontSize: 10, color: T.textShadow }}>Shift+Enter for new line · Enter to send</div>}
                  </div>
                </div>
                <button
                  onClick={isStreaming ? stopStreaming : sendMessage}
                  disabled={!isStreaming && (!input.trim() || loading)}
                  title={isStreaming ? "Stop generating" : "Send"}
                  style={{
                  padding: isMobile ? "14px 16px" : "12px 22px",
                  background: isStreaming ? "rgba(232,69,69,0.15)" : loading || !input.trim() ? T.bgElevated : TOKENS.gold,
                  color: isStreaming ? TOKENS.statusRed : loading || !input.trim() ? T.textFaint : "#fff",
                  border: `1px solid ${isStreaming ? TOKENS.statusRed : loading || !input.trim() ? T.border : TOKENS.gold}`,
                  borderRadius: 10, cursor: isStreaming ? "pointer" : loading || !input.trim() ? "not-allowed" : "pointer",
                  fontSize: isMobile ? 20 : 16, fontWeight: 700, height: isMobile ? 54 : 46, transition: "all 0.15s", flexShrink: 0,
                }}>
                  {isStreaming ? "◼" : loading ? "⟳" : chatMode === "propose" ? "✍" : "→"}
                </button>
              </div>
            </div>
    </>
  );
}
